import { FlatList, Image, StyleSheet, View, Pressable, useWindowDimensions } from "react-native";
import { FindifyTopBarOverlay } from "../../../genericPages/components/FindifyTopBarOverlay";
import React, { useContext, useEffect, useState } from "react";
import { PlaceColors } from "../../../genericPages/helpers/PlaceColors";
import { BasePage } from "../../../genericPages/helpers/BasePage";
import { useTranslation } from "react-i18next";
import { FindifySimpleHeader, SimpleHeaderAction } from "../../../genericPages/components/FindifySimpleHeader";
import { FindifyDataContext } from "@/contexts/FindifyDataContext";
import { makeIconFilterButton } from "../../../genericPages/helpers/Components";
import { FindifyBasicModal } from "../../../genericPages/components/FindifyBasicModal";
import { PlaceText } from "../../../genericPages/components/PlaceText";
import { BaseFindifyList } from "../../../genericPages/components/FindifyList";
import { getTranslatedString } from "../../../genericPages/helpers/FindifyTranslatedString";
import { PlaceListInitialUseEffect, PlaceListOnSelectedCategoryChangeEffect, loadContentsPageFromApi } from "@/layouts/genericPages/helpers/PlaceListHelper";
import { getContentIdForModule, getFeaturedImageUrl, makeContentDate, makeContentTitle } from "@/layouts/genericPages/helpers/DetailsHelpers";
import Icon from "react-native-vector-icons/Ionicons";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { FindifyFooterLogo, BottomNavigationPadding } from "../../../genericPages/components/FindifyNavigation";
import { FindifyAppHeader } from "../../../genericPages/components/FindifyAppHeader";
import { useSeoTitle } from "../../../genericPages/helpers/SeoTitle";

const isEnglishLanguage = (language?: string) => language?.startsWith('en');
const localizedText = (findifyDataContext: any, pt: string, en: string) => {
    return isEnglishLanguage(findifyDataContext?.language) ? en : pt;
};

const stripHtml = (html: string) => {
    return (html ?? '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();
}

export const PlaceContentListTab = ({ navigation, ...props }: any) => {
    const [contents, setContents] = useState([]);
    const { t } = useTranslation();
    const [module, setModule] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const findifyDataContext = useContext(FindifyDataContext);
    const [loaded, setLoaded] = useState(false);
    const [categoryFiltersModalOpen, setCategoriesModalOpen] = useState(false);
    const [availableModuleCategories, setAvailableModuleCategories] = useState<any[]>([]);
    const [actionsList, setActionsList] = useState<SimpleHeaderAction[]>([]);
    const insets = useSafeAreaInsets();
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;

    useEffect(() => {
        PlaceListInitialUseEffect({
            props,
            setContents,
            setLoaded,
            setModule,
            setSelectedCategories,
            findifyDataContext,
            setAvailableModuleCategories,
            contents,
        });
    }, [])


    useEffect(() => {
        PlaceListOnSelectedCategoryChangeEffect({
            loaded,
            selectedCategories,
            setContents,
            module,
            findifyDataContext,
            setActionsList,
            availableModuleCategories,
            t,
        });
    }, [selectedCategories])


    const header = <FindifySimpleHeader navigation={navigation}
        // module if no category filter or multiple category filter, single cat filter shows cat title
        title={
            selectedCategories.length === 1 ?
                getTranslatedString(
                    availableModuleCategories.find((cat: any) => cat.category_id === selectedCategories[0])?.name, findifyDataContext) :
                t('modules.content_types.' + module)
        }
        actions={actionsList}
        image={findifyDataContext?.workspace.app_settings?.app_banner_url} />;

    const openContent = (content: any) => {
        const contentId = getContentIdForModule(content, module);
        navigation.push('PlaceContentDetails', {
            module: module,
            content_id: contentId,
            page_slug: content.page_slug
        });
    }

    const isFindifyPoiList = Number(findifyDataContext?.workspace?.workspace_id) === 193 && module === 'points_of_interest';
    const isFindifyContentList = Number(findifyDataContext?.workspace?.workspace_id) === 193 && module === 'contents';
    const isFindifyStyledList = isFindifyPoiList || isFindifyContentList;
    const listTitle = selectedCategories.length === 1
        ? getTranslatedString(availableModuleCategories.find((cat: any) => cat.category_id === selectedCategories[0])?.name, findifyDataContext)
        : isFindifyContentList
            ? localizedText(findifyDataContext, 'Curiosidades', 'Curiosities')
            : localizedText(findifyDataContext, 'Pontos de Interesse', 'Points of Interest');
    useSeoTitle(listTitle || t('modules.content_types.' + module), 'Museu da Terra de Miranda');
    const heroImage = getFeaturedImageUrl(contents?.[0]) || findifyDataContext?.workspace?.app_settings?.app_banner_url;
    const colors = PlaceColors(findifyDataContext);

    if (isFindifyStyledList) {
        return <BasePage navigation={navigation}>
            <View style={[styles.findifyListPage, { backgroundColor: colors.background.get() }]}>
                <FindifyAppHeader findifyDataContext={findifyDataContext} navigation={navigation} allowBackFallback={false} />
                <FlatList
                    key={isLandscapeTablet ? 'findify-poi-grid' : 'findify-poi-list'}
                    data={contents}
                    numColumns={isLandscapeTablet ? 2 : 1}
                    columnWrapperStyle={isLandscapeTablet ? styles.findifyPoiGridRow : undefined}
                    keyExtractor={(item: any, index: number) => `${getContentIdForModule(item, module)}-${index}`}
                    showsVerticalScrollIndicator={false}
                    onEndReached={() => {
                        if (loaded) {
                            loadContentsPageFromApi(selectedCategories, contents, module, findifyDataContext, setContents);
                        }
                    }}
                    onEndReachedThreshold={0.7}
                    contentContainerStyle={{
                        width: '100%',
                        maxWidth: isLandscapeTablet ? 1120 : isTablet ? 760 : undefined,
                        alignSelf: 'center',
                        paddingHorizontal: isTablet ? 32 : 20,
                        paddingTop: isTablet ? 56 : 48,
                        paddingBottom: insets.bottom + BottomNavigationPadding + 24,
                    }}
                    ListHeaderComponent={() => (
                        <Pressable style={styles.findifyHeroCard} onPress={() => contents?.[0] ? openContent(contents[0]) : null}>
                            {heroImage ? <Image source={{ uri: heroImage }} resizeMode="cover" style={styles.findifyHeroImage} /> : null}
                            <View style={styles.findifyHeroOverlay} />
                            <View style={styles.findifyHeroGradient} />
                            <View style={styles.findifyHeroText}>
                                <PlaceText style={[styles.findifyHeroEyebrow, { color: colors.secondary.get() }]}>
                                    {isFindifyContentList
                                        ? localizedText(findifyDataContext, 'CONTEÚDOS DO MUSEU', 'MUSEUM STORIES')
                                        : localizedText(findifyDataContext, 'COLEÇÃO PERMANENTE', 'PERMANENT COLLECTION', 'COLLECTION PERMANENTE')}
                                </PlaceText>
                                <PlaceText style={styles.findifyHeroTitle}>{listTitle}</PlaceText>
                            </View>
                        </Pressable>
                    )}
                    ListFooterComponent={() => <FindifyFooterLogo findifyDataContext={findifyDataContext} />}
                    renderItem={({ item, index }: any) => {
                        const image = getFeaturedImageUrl(item);
                        const title = makeContentTitle(item, module, findifyDataContext);
                        const resume = stripHtml(getTranslatedString(item.resume || item.description, findifyDataContext));
                        const isFeaturedCard = !isLandscapeTablet && (index === 0 || index === 3);
                        return <Pressable
                            style={[
                                styles.findifyPoiCard,
                                isLandscapeTablet ? styles.findifyPoiCardGrid : null,
                                {
                                    backgroundColor: colors.background.solidAlphaWhiteBase(55),
                                    borderColor: colors.secondary.withAlpha(65),
                                },
                                isFeaturedCard ? styles.findifyPoiCardFeatured : null
                            ]}
                            onPress={() => openContent(item)}>
                            {image ? <Image source={{ uri: image }} resizeMode="cover" style={[styles.findifyPoiImage, isFeaturedCard ? styles.findifyPoiImageFeatured : null]} /> : null}
                            <View style={styles.findifyPoiBody}>
                                <PlaceText style={[styles.findifyPoiTitle, { color: colors.text.get() }]}>{title}</PlaceText>
                                {!!resume && <PlaceText numberOfLines={3} style={[styles.findifyPoiResume, { color: colors.text.withAlpha(190) }]}>{resume}</PlaceText>}
                                <View style={styles.findifyReadMoreRow}>
                                    <PlaceText style={[
                                        styles.findifyReadMore,
                                        {
                                            color: colors.primary.get(),
                                            borderBottomColor: colors.primary.withAlpha(80),
                                        }
                                    ]}>{localizedText(findifyDataContext, 'LER MAIS', 'READ MORE')}</PlaceText>
                                </View>
                            </View>
                        </Pressable>
                    }}
                />
                <Pressable style={[styles.findifyFilterButton, { bottom: insets.bottom + BottomNavigationPadding + 12, backgroundColor: colors.primary.get() }]} onPress={() => setCategoriesModalOpen(true)}>
                    <Icon name="funnel" size={20} color={colors.background.get()} />
                </Pressable>
                <FindifyBasicModal
                    isOpen={categoryFiltersModalOpen}
                    closeModal={() => {
                        setCategoriesModalOpen(false)
                    }}
                    style={{
                        width: '92%',
                        maxWidth: 440,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: colors.primary.withAlpha(45),
                        backgroundColor: colors.background.get(),
                        paddingHorizontal: 18,
                        paddingTop: 10,
                        paddingBottom: 18,
                        alignItems: 'stretch',
                    }}
                >
                        <View style={styles.findifyFilterModal}>
                            <View style={[styles.findifyFilterHandle, { backgroundColor: colors.primary.withAlpha(55) }]} />
                            <View style={styles.findifyFilterHeader}>
                                <View>
                                <PlaceText style={[styles.findifyFilterTitle, { color: colors.primary.get() }]}>
                                    {isFindifyContentList
                                        ? localizedText(findifyDataContext, 'Filtrar conteúdos', 'Filter stories')
                                        : localizedText(findifyDataContext, 'Filtrar pontos', 'Filter places')}
                                </PlaceText>
                                <PlaceText style={[styles.findifyFilterSubtitle, { color: colors.text.withAlpha(190) }]}>{localizedText(findifyDataContext, 'Escolha uma ou mais categorias', 'Choose one or more categories')}</PlaceText>
                            </View>
                            <Pressable style={[styles.findifyFilterClose, { backgroundColor: colors.primary.withAlpha(15) }]} onPress={() => setCategoriesModalOpen(false)}>
                                <Icon name="close-outline" size={26} color={colors.primary.get()} />
                            </Pressable>
                        </View>
                        {availableModuleCategories.map((cat: any, index: number) => {
                            const selected = selectedCategories.includes(cat.category_id);
                            return <Pressable
                                key={'avaliableLanguages' + index}
                                style={[
                                    styles.findifyFilterOption,
                                    {
                                        backgroundColor: colors.background.solidAlphaWhiteBase(35),
                                        borderColor: colors.primary.withAlpha(45),
                                    },
                                    selected ? {
                                        backgroundColor: colors.secondary.withAlpha(55),
                                        borderColor: colors.primary.get(),
                                    } : null
                                ]}
                                onPress={() => {
                                    if (selected) {
                                        setSelectedCategories(selectedCategories.filter((c: number) => c !== cat.category_id));
                                    } else {
                                        setSelectedCategories([...selectedCategories, cat.category_id]);
                                    }
                                }}>
                                <PlaceText style={[
                                    styles.findifyFilterOptionText,
                                    { color: selected ? colors.primary.get() : colors.text.get() },
                                ]}>{getTranslatedString(cat.name, findifyDataContext)}</PlaceText>
                                <Icon name={selected ? "checkmark-circle" : "ellipse-outline"} size={22} color={selected ? colors.primary.get() : colors.primary.withAlpha(70)} />
                            </Pressable>
                        })}
                        <View style={styles.findifyFilterActions}>
                            <Pressable
                                style={[styles.findifyFilterClearButton, { borderColor: colors.primary.get() }]}
                                onPress={() => setSelectedCategories([])}>
                                <PlaceText style={[styles.findifyFilterClearText, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Limpar', 'Clear')}</PlaceText>
                            </Pressable>
                            <Pressable
                                style={[styles.findifyFilterApplyButton, { backgroundColor: colors.primary.get() }]}
                                onPress={() => setCategoriesModalOpen(false)}>
                                <PlaceText style={[styles.findifyFilterApplyText, { color: colors.background.get() }]}>{localizedText(findifyDataContext, 'Aplicar', 'Apply')}</PlaceText>
                            </Pressable>
                        </View>
                    </View>
                </FindifyBasicModal>
            </View>
        </BasePage>
    }

    return <BasePage navigation={navigation}>
        <FindifyTopBarOverlay />
        <BaseFindifyList
            items={contents.map((content: any) => {
                const contentId = getContentIdForModule(content, module);
                return {
                    id: contentId,
                    image: getFeaturedImageUrl(content),
                    title: makeContentTitle(content, module, findifyDataContext),
                    resume: getTranslatedString(content.resume, findifyDataContext),
                    date: makeContentDate(content, module),
                    onPress: () => {
                        navigation.push('PlaceContentDetails', {
                            module: module,
                            content_id: contentId,
                            page_slug: content.page_slug
                        })
                    }
                }
            })}
            header={header}
            onEndReached={() => {
                if (loaded) {
                    loadContentsPageFromApi(selectedCategories, contents, module, findifyDataContext, setContents);
                }
            }}
            enableGrid={true}
            selectLabel={t('list.read_more')}
            actions={<>
                {makeIconFilterButton(() => setCategoriesModalOpen(true), "funnel-outline", selectedCategories.length !== 0, findifyDataContext)}
            </>}
        />

        <FindifyBasicModal
            isOpen={categoryFiltersModalOpen}
            closeModal={() => {
                setCategoriesModalOpen(false)
            }}
        >
            <View style={styles.optionsZoneOptionsModal}>
                {availableModuleCategories.map((cat: any, index: number) => {
                    return <Pressable
                        key={'avaliableLanguages' + index}
                        onPress={() => {
                            // findifyDataContext?.setLanguage(lang.tag);
                            if (selectedCategories.includes(cat.category_id)) {
                                setSelectedCategories(selectedCategories.filter((c: number) => c !== cat.category_id));
                            } else {
                                setSelectedCategories([...selectedCategories, cat.category_id]);
                            }

                        }}>
                        <View style={{
                            ...styles.optionsZoneOptionModalLine,
                            ...(index !== 0 ? styles.optionTopBorder : {}),
                            ...(selectedCategories.includes(cat.category_id) ? { opacity: 0.6 } : {})
                        }}>
                            <PlaceText style={{ color: PlaceColors(findifyDataContext).text.get() }} >{getTranslatedString(cat.name, findifyDataContext)}</PlaceText>
                        </View>
                    </Pressable>
                })}
            </View>
        </FindifyBasicModal>
    </BasePage >;
};


const styles = StyleSheet.create({

    container: {
        height: '100%',
        width: '100%',
    },
    map: {
        flex: 1
    },
    optionTopBorder: {
        borderTopColor: "#00000060",
        borderTopWidth: 1
    },
    optionsZoneOptionsModal: {

    },
    optionsZoneOptionModalLine: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 15,
        padding: 10,
        // width: '90%'

    },
    optionsZoneDivider: {
        width: '80%',
        marginLeft: '10%',
        marginRight: '10%',
        marginTop: 10,
        height: 2,
        borderRadius: 6,
        backgroundColor: '#00000030'
    },
    findifyListPage: {
        flex: 1,
    },
    findifyListHeader: {
        height: 62,
        paddingHorizontal: 24,
        borderBottomWidth: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    findifyHeaderIconButton: {
        width: 42,
        height: 42,
        alignItems: 'center',
        justifyContent: 'center',
    },
    findifyHeaderLogo: {
        width: 154,
        height: 38,
    },
    findifyHeaderActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    findifyAccessibilityButton: {
        width: 42,
        height: 42,
        borderRadius: 11,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    findifyHeroCard: {
        height: 400,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 48,
        backgroundColor: '#111',
    },
    findifyHeroImage: {
        width: '100%',
        height: '100%',
    },
    findifyHeroOverlay: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.18)',
    },
    findifyHeroGradient: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: '55%' as any,
        backgroundColor: 'rgba(0,0,0,0.46)',
    },
    findifyHeroText: {
        position: 'absolute',
        left: 28,
        right: 24,
        bottom: 30,
    },
    findifyHeroEyebrow: {
        fontSize: 11,
        lineHeight: 16,
        letterSpacing: 1.6,
        fontWeight: '700',
        marginBottom: 8,
    },
    findifyHeroTitle: {
        color: '#fff',
        fontFamily: 'Bitter',
        fontSize: 46,
        lineHeight: 52,
        fontWeight: '700',
    },
    findifyPoiCard: {
        borderWidth: 1,
        marginBottom: 24,
        overflow: 'hidden',
    },
    findifyPoiGridRow: {
        gap: 24,
        alignItems: 'stretch',
    },
    findifyPoiCardGrid: {
        flex: 1,
        minWidth: 0,
    },
    findifyPoiCardFeatured: {
        marginBottom: 28,
    },
    findifyPoiImage: {
        width: '100%',
        height: 260,
    },
    findifyPoiImageFeatured: {
        height: 300,
    },
    findifyPoiBody: {
        paddingHorizontal: 24,
        paddingTop: 24,
        paddingBottom: 20,
        minHeight: 150,
    },
    findifyPoiTitle: {
        fontFamily: 'Bitter',
        fontSize: 24,
        lineHeight: 32,
        fontWeight: '700',
        marginBottom: 12,
    },
    findifyPoiResume: {
        fontSize: 14,
        lineHeight: 20,
    },
    findifyReadMoreRow: {
        alignItems: 'flex-end',
        marginTop: 20,
    },
    findifyReadMore: {
        fontSize: 12,
        lineHeight: 16,
        fontWeight: '700',
        letterSpacing: 1.1,
        borderBottomWidth: 1,
        paddingBottom: 2,
    },
    findifyFilterButton: {
        position: 'absolute',
        right: 24,
        width: 56,
        height: 56,
        borderRadius: 28,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.22,
        shadowRadius: 8,
        elevation: 8,
    },
    findifyFilterModal: {
        width: '100%',
    },
    findifyFilterHandle: {
        width: 46,
        height: 4,
        borderRadius: 4,
        alignSelf: 'center',
        marginBottom: 16,
    },
    findifyFilterHeader: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 16,
    },
    findifyFilterTitle: {
        fontFamily: 'Bitter',
        fontSize: 25,
        lineHeight: 30,
        fontWeight: '700',
    },
    findifyFilterSubtitle: {
        fontSize: 12,
        lineHeight: 18,
        marginTop: 4,
    },
    findifyFilterClose: {
        width: 38,
        height: 38,
        borderRadius: 19,
        alignItems: 'center',
        justifyContent: 'center',
    },
    findifyFilterOption: {
        minHeight: 48,
        borderRadius: 7,
        borderWidth: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 14,
        paddingVertical: 10,
        marginBottom: 10,
    },
    findifyFilterOptionSelected: {
    },
    findifyFilterOptionText: {
        fontSize: 14,
        lineHeight: 19,
        fontWeight: '600',
        flex: 1,
        paddingRight: 10,
    },
    findifyFilterOptionTextSelected: {},
    findifyFilterActions: {
        flexDirection: 'row',
        gap: 10,
        marginTop: 8,
    },
    findifyFilterClearButton: {
        flex: 1,
        minHeight: 48,
        borderRadius: 6,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    findifyFilterClearText: {
        fontSize: 14,
        fontWeight: '700',
    },
    findifyFilterApplyButton: {
        flex: 1,
        minHeight: 48,
        borderRadius: 6,
        alignItems: 'center',
        justifyContent: 'center',
    },
    findifyFilterApplyText: {
        fontSize: 14,
        fontWeight: '700',
    }
})
